import { UseQueryOptions, useQuery } from 'react-query';
import { db as StardogDb } from 'stardog';

import {
  STARDOG_CONTEXT_ALL_IRI,
  STARDOG_CONTEXT_LOCAL_IRI,
  STARDOG_CONTEXT_NAMED_IRI,
  STARDOG_CONTEXT_VIRTUAL_IRI,
} from 'bones/constants/iris';
import { DEFAULT_MODEL_NAME } from 'bones/constants/model';
import { VoiceboxDatabase } from 'bones/constants/voicebox/settings';
import { runWithRefetchConnectionToken } from 'bones/utils/portal/refetchConnectionToken';
import { useStardogConnectionFromPortalIndex } from 'bones/utils/portal/useStardogConnectionFromPortalIndex';
import { alphaSorter } from 'bones/utils/sort/alphaSort';

const INVALID_MODEL_GRAPHS = new Set([
  '*',
  STARDOG_CONTEXT_ALL_IRI,
  STARDOG_CONTEXT_LOCAL_IRI,
  STARDOG_CONTEXT_NAMED_IRI,
  STARDOG_CONTEXT_VIRTUAL_IRI,
]);

export const usePortalVoiceboxDatabases = (
  connectionIdx: number,
  options?: UseQueryOptions<Record<string, VoiceboxDatabase> | null>
) => {
  const stardogConnection = useStardogConnectionFromPortalIndex(connectionIdx);
  return useQuery<Record<string, VoiceboxDatabase> | null>(
    `usePortalVoiceboxDatabases-${stardogConnection ? connectionIdx : null}`,
    async () => {
      if (!stardogConnection) {
        return null;
      }

      const response = await runWithRefetchConnectionToken(
        stardogConnection,
        connectionIdx,
        (stardogConn) => StardogDb.listInfo(stardogConn)
      );

      if (!response.ok) {
        throw new Error(response.statusText || String(response.status));
      }

      const databasesById: Record<string, VoiceboxDatabase> = {};
      response.body?.databases?.forEach(
        (data: Record<string, any> | undefined) => {
          if (!data) {
            return;
          }

          const databaseId: string = data['database.name'] || '';
          if (!databaseId) {
            return;
          }

          if (!data['database.online'] || !data['voicebox.enabled']) {
            return;
          }

          const modelGraphs: Record<string, Set<string>> = {};

          const reasoningSchemas: string[] = data['reasoning.schemas'] || [];
          const models = Array.from(
            reasoningSchemas.reduce<Set<string>>((acc, reasoningSchema) => {
              const [model, ...graphParts] = reasoningSchema.split('=');
              if (!model || model === DEFAULT_MODEL_NAME) {
                return acc;
              }

              const graph = graphParts.join('=');
              if (INVALID_MODEL_GRAPHS.has(graph)) {
                delete modelGraphs[model];
                acc.delete(model);
                return acc;
              }

              if (!modelGraphs[model]) {
                modelGraphs[model] = new Set();
              }

              modelGraphs[model].add(graph);
              acc.add(model);
              return acc;
            }, new Set())
          );
          models.sort(alphaSorter);

          const reasoningSchemaGraphs: string[] =
            data['reasoning.schema.graphs'] || [];
          if (
            !reasoningSchemaGraphs.some((graph) =>
              INVALID_MODEL_GRAPHS.has(graph)
            )
          ) {
            modelGraphs[DEFAULT_MODEL_NAME] = new Set(reasoningSchemaGraphs);
            models.unshift(DEFAULT_MODEL_NAME);
          }

          const namespacesArr: string[] = data['database.namespaces'] || [];
          const namespaces = namespacesArr.reduce<Record<string, string>>(
            (acc, namespace) => {
              const [prefix, ...rest] = namespace.split('=');
              acc[prefix] = rest.join('=');
              return acc;
            },
            {}
          );

          const examples: string[] = data['voicebox.example.questions'] || [];
          const isMediaEnabled: boolean = data['media.enabled'] || false;

          databasesById[databaseId] = {
            examples,
            id: databaseId,
            isMediaEnabled,
            modelGraphs: Object.fromEntries(
              Object.entries(modelGraphs).map(([model, graphs]) => [
                model,
                Array.from(graphs),
              ])
            ),
            models,
            namespaces,
          };
        },
        []
      );

      return databasesById;
    },
    options
  );
};
